import {
  faWarning,
  faCalendarClock,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons'
import { Alert, Anchor, Box, Checkbox, Flex, Tooltip } from '@mantine/core'
import { Text } from '@mantine/core'
import { isBefore } from 'date-fns'
import { useEffect, useState } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useQueryGetAllBookingInsurancesQuote } from '@flecto/core/saas'
import { useTranslation } from '@flecto/translations'
import {
  type ParameterID,
  type CoreBooking,
  type CoreInsuranceResponseList,
} from '@flecto/types'
import { Icon, Modal, TextCurrency } from '@flecto/ui'

import { CardBookingQuickDetails } from '../../../bookings'
import {
  type HandleBookingDatesPayload,
  useUpdateBookingDates,
} from '../../hooks/useUpdateBookingDates'

export type ModalBookingDatesExtensionConfirmationProps = {
  bookingId?: ParameterID
  newDates: HandleBookingDatesPayload
  insurances: CoreInsuranceResponseList
  booking?: CoreBooking
}

export function ModalBookingDatesExtensionConfirmation({
  bookingId,
  newDates,
  insurances,
  booking,
}: ModalBookingDatesExtensionConfirmationProps) {
  const { t } = useTranslation('saas', { keyPrefix: 'booking_dates' })
  const { t: tActions } = useTranslation('shared', { keyPrefix: 'actions' })
  const track = useAnalytics('segment')

  const [forceSuggestionPrice, setForceSuggestionPrice] = useState(false)

  const isDateBefore =
    booking?.end_at && newDates?.end_at
      ? isBefore(new Date(newDates.end_at), new Date(booking.end_at))
      : false
  const { handleUpdateBookingDates, isLoading } = useUpdateBookingDates({
    bookingId,
  })

  const { data: quotes, isLoading: isLoadingQuotes } =
    useQueryGetAllBookingInsurancesQuote({
      bookingId,
      end_at: newDates.end_at,
    })
  const quoteTotalCents = quotes?.quote_total_cents ?? 0

  useEffect(() => {
    track('BOOKING__CHANGE_DATES_MODAL', {
      booking_id: String(bookingId),
    })
  }, [])

  return (
    <Modal size='md'>
      <Modal.Close />
      <Modal.Icon icon={faCalendarClock} color='orange' withCircleBackground />
      <Modal.Title>{t('modal_dates_extension_title')}</Modal.Title>
      <Modal.Subtitle>{t('modal_dates_extension_description')}</Modal.Subtitle>
      <Modal.Content>
        {booking?.insured &&
          booking.meta?.has_issued_insurances &&
          !isDateBefore && (
            <Alert
              color='orange'
              icon={<Icon icon={faWarning} color='orange' />}
            >
              <Text color='orange.7' fw={500}>
                {t('alert_description')} {quoteTotalCents > 0 ? '+' : ' '}
                <TextCurrency
                  cents={quoteTotalCents}
                  color='orange.7'
                  fw={500}
                  display={'inline'}
                />{' '}
                {t('alert_description_two')}{' '}
                <Anchor
                  color='orange.7'
                  href='/profile/billing'
                  target='_blank'
                  rel='noreferrer'
                  td='underline'
                >
                  {t('alert_description_link')}
                </Anchor>
              </Text>
            </Alert>
          )}

        <CardBookingQuickDetails
          mt='lg'
          bookingId={bookingId}
          previewNewEndAt={newDates.end_at}
          previewNewStartAt={newDates.start_at}
          insurances={insurances}
          quotes={quotes}
          isLoadingQuotes={isLoadingQuotes}
          withDates
        />

        <Flex gap='xs' mt='md' align='center'>
          <Checkbox
            onChange={() => setForceSuggestionPrice(!forceSuggestionPrice)}
            label={t('recalculate_prices')}
          />
          <Tooltip label={t('recalculate_prices_tooltip')} multiline>
            <Box>
              <Icon icon={faInfoCircle} />
            </Box>
          </Tooltip>
        </Flex>
      </Modal.Content>
      <Modal.Cancel />
      <Modal.Submit
        onClick={() => {
          handleUpdateBookingDates({
            start_at: newDates.start_at,
            end_at: newDates.end_at,
            force_suggested_booking_cost: forceSuggestionPrice,
          })
          track('BOOKING__CHANGE_DATES_CONFIRM', {
            booking_id: String(bookingId),
            recalculatePrices: forceSuggestionPrice,
          })
        }}
        loading={isLoading}
      >
        {tActions('confirm')}
      </Modal.Submit>
    </Modal>
  )
}
